/* ==========================================================================
   Spark — behaviour for the code-first homepage template
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies. Every hook is a data attribute
   (data-spark-*) so markup and behaviour stay greppable. All markup is
   server-rendered by Liquid; this file only toggles state — the page is
   fully readable with JS disabled (html.no-js styles cover the fallback).
   ========================================================================== */

(function () {
  "use strict";

  document.documentElement.classList.remove("no-js");

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  /* ------------------------------------------------------------------
     Header: transparent over the hero, solid once scrolled
     ------------------------------------------------------------------ */
  var header = document.querySelector("[data-spark-header]");
  if (header) {
    var onScroll = function () {
      header.classList.toggle("is-scrolled", window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     Overlays: mobile menu and search modal.
     Triggers keep aria-expanded in sync; focus moves into the dialog on
     open, is trapped inside while open, and returns to the trigger on
     close.
     ------------------------------------------------------------------ */
  function bindOverlay(name, hash) {
    var overlay = document.querySelector("[data-spark-" + name + "]");
    if (!overlay) return;

    var triggers = document.querySelectorAll("[data-spark-" + name + "-open]");
    var lastTrigger = null;

    var setOpen = function (open) {
      overlay.classList.toggle("is-open", open);
      triggers.forEach(function (btn) {
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
      if (open) {
        var focusTarget =
          overlay.querySelector("[data-spark-autofocus]") ||
          overlay.querySelector("a[href], button, input");
        if (focusTarget) focusTarget.focus();
      } else if (lastTrigger) {
        lastTrigger.focus();
      }
    };

    triggers.forEach(function (btn) {
      btn.setAttribute("aria-expanded", "false");
      btn.addEventListener("click", function (event) {
        event.preventDefault(); // anchor triggers fall back to a link without JS
        lastTrigger = btn;
        setOpen(true);
      });
    });

    overlay
      .querySelectorAll("[data-spark-" + name + "-close]")
      .forEach(function (el) {
        el.addEventListener("click", function () {
          setOpen(false);
        });
      });

    // Click on the backdrop (not the panel) closes; Escape closes; Tab is
    // trapped inside the open overlay.
    overlay.addEventListener("click", function (event) {
      if (event.target === overlay) setOpen(false);
    });
    document.addEventListener("keydown", function (event) {
      if (!overlay.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key === "Tab") {
        var focusables = overlay.querySelectorAll(
          'a[href], button, input, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    /* Deep link. The quiz has no page of its own — it's a modal rendered on
       every page by layout/spark.liquid — so until now there was no address
       anyone could send someone to. With a hash passed here, /#quiz opens it
       from anywhere: paste it in an email, an ad, a QR code, any page path.

       Checked on load AND on hashchange, so it works pasted cold and when a
       link on the current page points at it. The fragment is then dropped via
       replaceState (not pushState — it must not land in history) so a refresh
       or a Back into the page doesn't silently reopen the modal over whatever
       the shopper came back to read. */
    if (hash) {
      var hashTarget = "#" + hash;
      var openFromHash = function () {
        if (window.location.hash.toLowerCase() !== hashTarget) return;
        setOpen(true);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
      };
      window.addEventListener("hashchange", openFromHash);
      openFromHash();
    }
  }

  bindOverlay("menu");
  bindOverlay("search");
  bindOverlay("quiz-modal", "quiz");
  /* Trial choice modal. Only rendered where the header offers a choice (see
     trial_choice in snippets/spark-header.liquid) — bindOverlay no-ops when the
     overlay is absent, so the single-trial LPs need nothing here. Bound AFTER
     "menu" on purpose: the mobile menu CTA carries both [data-spark-menu-close]
     and [data-spark-trial-modal-open], and listeners fire in registration order,
     so the menu closes first and the dialog then takes focus instead of the menu
     bouncing focus back to the burger. /#trial deep-links it. */
  bindOverlay("trial-modal", "trial");

  /* ------------------------------------------------------------------
     Chat CTA — [data-spark-chat-open] opens the Aurora widget.

     The widget is the Aurora bundle, mounted by
     snippets/chat-widget.liquid, and the only thing it puts on
     window.ChatWidget is `mount`. There is no open()/toggle() to call, so
     the button drives the widget's OWN controls, keyed off the state classes
     its bundle renders:

       .chat-minimized-container      the bubble; carries `hidden` when the
                                      panel is open
       button.chat-maximized-btn      inside it — the widget's own open action
       .chat-panel.open               the expanded panel

     Clicking the widget's real button (rather than faking state) means the
     widget stays the source of truth: whatever it does on open — analytics,
     socket connect, focus — happens exactly as if the shopper had tapped the
     bubble themselves.

     `hidden` here is opacity+visibility, NOT display, so the container keeps
     its box and offsetParent stays non-null. The class is the only reliable
     read of whether the bubble is really showing.

     The widget mounts async off a CDN with a 30s recovery budget, so a tap
     landing before it is ready has to wait rather than no-op silently: retry
     briefly, and mark the button busy so the shopper sees something happened.
     ------------------------------------------------------------------ */
  var chatTriggers = document.querySelectorAll("[data-spark-chat-open]");
  if (chatTriggers.length) {
    var openAurora = function () {
      var root = document.getElementById("shop-chat-widget");
      if (!root) return false;

      var minimized = root.querySelector(".chat-minimized-container");
      if (minimized && !minimized.classList.contains("hidden")) {
        var bubble = minimized.querySelector(".chat-maximized-btn");
        if (bubble) {
          bubble.click();
          return true;
        }
      }

      // Already open — don't toggle it shut, just put the cursor in it.
      var panel = root.querySelector(".chat-panel.open");
      if (panel) {
        var panelInput = panel.querySelector("input, textarea");
        if (panelInput) panelInput.focus();
        return true;
      }

      // Inline input-bar state (no bubble, no panel): focus the input so the
      // shopper can type straight away — on mobile that raises the keyboard.
      var inlineInput = root.querySelector("#chatInput");
      if (inlineInput) {
        inlineInput.focus();
        return true;
      }
      return false;
    };

    chatTriggers.forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (openAurora()) return;
        // Not mounted yet. Poll for it, capped, with the button showing busy.
        var waited = 0;
        var step = 150;
        var budget = 8000;
        btn.setAttribute("aria-busy", "true");
        var poll = setInterval(function () {
          waited += step;
          if (openAurora() || waited >= budget) {
            clearInterval(poll);
            btn.removeAttribute("aria-busy");
            if (waited >= budget) {
              console.warn("[Chat CTA] Aurora widget never mounted");
            }
          }
        }, step);
      });
    });
  }

  /* ------------------------------------------------------------------
     Mobile menu submenus: accordions inside the [data-spark-menu]
     drawer. Each expandable top-level item is a .spark-menu__item
     wrapping a [data-spark-submenu-toggle] button and a
     [data-spark-submenu] panel; the open state is class .is-open on the
     wrapper (CSS shows/hides the panel). Opening one is independent —
     siblings stay as they are, since the drawer scrolls and a senior
     reader may want several sections open at once. This markup only
     exists in the mobile drawer, so on desktop the queries match nothing.
     ------------------------------------------------------------------ */
  var sparkMenu = document.querySelector("[data-spark-menu]");
  if (sparkMenu) {
    // Collapse every open submenu and re-sync its toggle. Called on each
    // drawer close so reopening always starts from a clean, all-collapsed
    // state (independent of how the drawer was closed).
    var collapseSubmenus = function () {
      sparkMenu
        .querySelectorAll(".spark-menu__item.is-open")
        .forEach(function (item) {
          item.classList.remove("is-open");
          var toggle = item.querySelector("[data-spark-submenu-toggle]");
          if (toggle) toggle.setAttribute("aria-expanded", "false");
        });
    };

    sparkMenu
      .querySelectorAll("[data-spark-submenu-toggle]")
      .forEach(function (toggle) {
        toggle.addEventListener("click", function (event) {
          event.preventDefault(); // it's a <button>, but never navigate
          var item = toggle.closest(".spark-menu__item");
          if (!item) return;
          var open = item.classList.toggle("is-open");
          toggle.setAttribute("aria-expanded", open ? "true" : "false");
        });
      });

    // The drawer open/close lives in bindOverlay("menu"); rather than
    // reach into it, we listen on the same close paths and collapse after
    // each. Harmless when nothing is open. Any close trigger:
    sparkMenu
      .querySelectorAll("[data-spark-menu-close]")
      .forEach(function (el) {
        el.addEventListener("click", collapseSubmenus);
      });
    // Backdrop click (target is the overlay itself):
    sparkMenu.addEventListener("click", function (event) {
      if (event.target === sparkMenu) collapseSubmenus();
    });
    // Escape while the drawer is open:
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && sparkMenu.classList.contains("is-open")) {
        collapseSubmenus();
      }
    });
  }

  /* ------------------------------------------------------------------
     Desktop mega-menu: nav items with child links reveal a full-width
     panel under the sticky header. Desktop only — below 1024px the nav
     (and its panels) are display:none, so the open path is gated on a
     live matchMedia check rather than bailing at load time: listeners
     stay bound so a resize/rotation across the breakpoint behaves.

     Hover-intent opens after a short dwell (60 ms) and cancels on a
     quick pass-through; moving between items switches panels. Because
     each panel is a DOM descendant of its item (and the item of the
     header), the pointer travelling item -> panel never leaves either
     element, so nothing closes mid-reach; leaving the whole header
     closes after a 150 ms grace. Keyboard: focus opens, Escape closes
     and returns focus to the trigger, focus leaving the header closes.
     Exactly one panel is open at a time; the header carries
     .is-meganav-open whenever any panel is open so it reads solid.
     ------------------------------------------------------------------ */
  var meganavHeader = document.querySelector("[data-spark-header]");
  var meganavItems = meganavHeader
    ? meganavHeader.querySelectorAll("[data-spark-meganav-item]")
    : [];
  if (meganavHeader && meganavItems.length) {
    var meganavMq = window.matchMedia("(min-width: 1280px)");
    var meganavOpenItem = null;
    var meganavOpenTimer = null;
    var meganavCloseTimer = null;
    var meganavOpenScrollY = 0;

    var meganavTrigger = function (item) {
      return item.querySelector("[data-spark-meganav-trigger]");
    };

    // The single source of truth: open `item` (or null to close all),
    // keeping .is-open, aria-expanded and the header flag in sync.
    var meganavSet = function (item) {
      // Live breakpoint gate: never open below the nav breakpoint (the
      // burger world owns that width), but always allow closing.
      if (item && !meganavMq.matches) return;
      if (meganavOpenItem === item) return;
      if (meganavOpenItem) {
        meganavOpenItem.classList.remove("is-open");
        var prev = meganavTrigger(meganavOpenItem);
        if (prev) prev.setAttribute("aria-expanded", "false");
      }
      meganavOpenItem = item;
      if (item) {
        item.classList.add("is-open");
        meganavOpenScrollY = window.scrollY;
        var next = meganavTrigger(item);
        if (next) next.setAttribute("aria-expanded", "true");
      }
      meganavHeader.classList.toggle("is-meganav-open", !!item);
    };

    var meganavClearTimers = function () {
      clearTimeout(meganavOpenTimer);
      clearTimeout(meganavCloseTimer);
    };

    meganavItems.forEach(function (item) {
      var trigger = meganavTrigger(item);
      if (trigger) trigger.setAttribute("aria-expanded", "false");

      // Clicking the trigger opens/closes the panel instead of following its
      // href to the collection — the label's job is to reveal the menu. The
      // href stays as the no-JS fallback. (Below the nav breakpoint the whole
      // desktop nav is hidden and the burger menu takes over.)
      if (trigger) {
        trigger.addEventListener("click", function (event) {
          if (!meganavMq.matches) return;
          event.preventDefault();
          meganavClearTimers();
          meganavSet(meganavOpenItem === item ? null : item);
        });
      }

      item.addEventListener("mouseenter", function () {
        meganavClearTimers();
        meganavOpenTimer = setTimeout(function () {
          meganavSet(item);
        }, 60);
      });
      item.addEventListener("mouseleave", function () {
        clearTimeout(meganavOpenTimer); // a quick pass-through never opens
      });

      // Keyboard: focus entering the item opens its panel at once.
      item.addEventListener("focusin", function () {
        meganavClearTimers();
        meganavSet(item);
      });
    });

    meganavHeader.addEventListener("mouseleave", function () {
      clearTimeout(meganavCloseTimer);
      meganavCloseTimer = setTimeout(function () {
        meganavSet(null);
      }, 150);
    });
    meganavHeader.addEventListener("mouseenter", function () {
      clearTimeout(meganavCloseTimer);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && meganavOpenItem) {
        var trigger = meganavTrigger(meganavOpenItem);
        meganavSet(null);
        if (trigger) trigger.focus();
      }
    });

    // Focus moving outside the header closes (without stealing focus).
    meganavHeader.addEventListener("focusout", function (event) {
      if (!meganavHeader.contains(event.relatedTarget)) meganavSet(null);
    });

    // Any pointer interaction outside the header closes an open panel.
    document.addEventListener("pointerdown", function (event) {
      if (meganavOpenItem && !meganavHeader.contains(event.target)) {
        meganavSet(null);
      }
    });

    // Crossing below the breakpoint (narrow / rotate) force-closes any
    // open panel so a stale hover panel never lingers into burger land.
    var meganavOnMqChange = function (event) {
      if (!event.matches) meganavSet(null);
    };
    if (meganavMq.addEventListener) {
      meganavMq.addEventListener("change", meganavOnMqChange);
    } else if (meganavMq.addListener) {
      meganavMq.addListener(meganavOnMqChange); // Safari < 14
    }

    // Scrolling the page with a panel open feels broken under a sticky
    // header, so close it — but ignore sub-pixel jitter, and do nothing
    // when no panel is open.
    window.addEventListener(
      "scroll",
      function () {
        if (!meganavOpenItem) return;
        if (Math.abs(window.scrollY - meganavOpenScrollY) > 4) {
          meganavSet(null);
        }
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------
     Search: live product suggestions from Shopify's predictive search
     API (/search/suggest.json), rendered inside the modal as the user
     types — no navigation. Enter / the Search button re-run the inline
     search; the suggestion chips prefill and search immediately. The
     form's action attribute stays the no-JS fallback (real /search).
     ------------------------------------------------------------------ */
  var searchForm = document.querySelector("[data-spark-search-form]");
  if (searchForm && window.fetch) {
    var searchInput = searchForm.querySelector("[data-spark-search-input]");
    var searchResults = document.querySelector("[data-spark-search-results]");
    var searchChips = document.querySelector("[data-spark-search-chips]");
    var searchSeq = 0; // stale-response guard: only the latest render wins
    var searchTimer = null;

    // Chips are the empty-state: they make way for results and come back
    // when the query is cleared. (The "Take the quiz" button below them
    // is not part of this — it stays either way.)
    var setChipsVisible = function (visible) {
      if (searchChips) searchChips.classList.toggle("hidden", !visible);
    };

    var renderSearchMessage = function (text) {
      searchResults.innerHTML = "";
      var p = document.createElement("p");
      p.className = "m-0 px-2 py-3 text-body-sm text-muted-foreground";
      p.textContent = text;
      searchResults.appendChild(p);
      searchResults.classList.remove("hidden");
      setChipsVisible(false);
    };

    // Empty state: nothing matched. Say so, then point to the Help Center.
    var renderNoResults = function (query) {
      searchResults.innerHTML = "";
      var p = document.createElement("p");
      p.className = "m-0 px-2 pt-3 pb-1 text-body-sm text-muted-foreground";
      p.textContent = "No matches for “" + query + "”.";
      searchResults.appendChild(p);
      var help = document.createElement("a");
      help.href = "/pages/faq";
      help.className =
        "flex items-center gap-2 rounded-[14px] px-2 py-2 text-body-sm font-semibold text-primary-text hover:bg-muted";
      help.textContent = "Can’t find it? Visit the Help Center →";
      searchResults.appendChild(help);
      searchResults.classList.remove("hidden");
      setChipsVisible(false);
    };

    // The catalog sells colorways as separate products ("… - Black",
    // "… - Grey") and prices vary by size, so raw predictive results
    // repeat near-identical rows. Group by base title alone and show the
    // group's lowest price; only a recognized color suffix is stripped
    // (a blind " - X" strip would merge genuinely different products
    // like "Bladder Leak Pads - Overnight").
    var SEARCH_COLOR_WORDS =
      "black grey gray beige white tan nude sand navy blush".split(" ");
    var splitSearchTitle = function (title) {
      var idx = title.lastIndexOf(" - ");
      if (idx > -1) {
        var suffix = title.slice(idx + 3).trim().toLowerCase();
        if (SEARCH_COLOR_WORDS.indexOf(suffix) > -1) {
          return { base: title.slice(0, idx).trim(), color: suffix };
        }
      }
      return { base: title, color: null };
    };

    var groupSearchProducts = function (products) {
      var groups = [];
      var byKey = {};
      products.forEach(function (product) {
        var parts = splitSearchTitle(product.title);
        var key = parts.base.toLowerCase();
        var price = parseFloat(product.price);
        var group = byKey[key];
        if (!group) {
          group = {
            product: product,
            title: parts.base,
            price: product.price,
            minPrice: isNaN(price) ? Infinity : price,
            members: 0,
            colorNames: [],
          };
          byKey[key] = group;
          groups.push(group);
        } else if (!isNaN(price) && price < group.minPrice) {
          group.minPrice = price;
          group.price = product.price;
        }
        group.members++;
        if (parts.color && group.colorNames.indexOf(parts.color) === -1) {
          group.colorNames.push(parts.color);
        }
      });
      return groups;
    };

    // "· 2 colors" when colorways merged; a lone color name only when the
    // entire group is that colorway (a mixed base+color group would make
    // "· Black" misleading — its low price may belong to the base).
    var searchGroupHint = function (group) {
      if (group.colorNames.length > 1) {
        return " · " + group.colorNames.length + " colors";
      }
      if (group.colorNames.length === 1 && group.members === 1) {
        var color = group.colorNames[0];
        return " · " + color.charAt(0).toUpperCase() + color.slice(1);
      }
      return "";
    };

    var renderSearchProducts = function (products, articles, query) {
      searchResults.innerHTML = "";
      groupSearchProducts(products)
        .slice(0, 6)
        .forEach(function (group) {
          var product = group.product;
          var item = document.createElement("a");
          item.href = product.url;
          item.className =
            "flex items-center gap-3.5 rounded-[14px] px-2 py-2 hover:bg-muted";
          var imageUrl =
            (product.featured_image && product.featured_image.url) ||
            product.image;
          if (imageUrl) {
            var img = document.createElement("img");
            img.className =
              "h-14 w-14 flex-none rounded-[10px] border border-card-border bg-background object-cover mix-blend-multiply";
            img.src =
              imageUrl +
              (imageUrl.indexOf("?") === -1 ? "?" : "&") +
              "width=112";
            img.alt = "";
            img.width = 56;
            img.height = 56;
            item.appendChild(img);
          }
          var info = document.createElement("span");
          info.className = "min-w-0 flex-1";
          var title = document.createElement("span");
          title.className = "block text-body leading-snug font-semibold";
          title.textContent = group.title;
          info.appendChild(title);
          if (group.price) {
            var price = document.createElement("span");
            price.className = "block text-body-sm text-muted-foreground";
            price.textContent = "From $" + group.price + searchGroupHint(group);
            info.appendChild(price);
          }
          item.appendChild(info);
          searchResults.appendChild(item);
        });
      // Blog articles (predictive search now requests type=product,article).
      if (articles && articles.length) {
        var blogHead = document.createElement("p");
        blogHead.className =
          "mx-2 mt-3 mb-1 text-caption font-semibold tracking-[0.1em] text-muted-foreground uppercase";
        blogHead.textContent = "From the blog";
        searchResults.appendChild(blogHead);
        articles.slice(0, 4).forEach(function (article) {
          var link = document.createElement("a");
          link.href = article.url;
          link.className =
            "flex items-center gap-3.5 rounded-[14px] px-2 py-2 hover:bg-muted";
          var aImg =
            article.image ||
            (article.featured_image && article.featured_image.url);
          if (aImg) {
            var aim = document.createElement("img");
            aim.className =
              "h-14 w-14 flex-none rounded-[10px] border border-card-border bg-background object-cover";
            aim.src =
              aImg + (aImg.indexOf("?") === -1 ? "?" : "&") + "width=112";
            aim.alt = "";
            aim.width = 56;
            aim.height = 56;
            link.appendChild(aim);
          }
          var ainfo = document.createElement("span");
          ainfo.className = "min-w-0 flex-1";
          var atitle = document.createElement("span");
          atitle.className = "block text-body leading-snug font-semibold";
          atitle.textContent = article.title;
          ainfo.appendChild(atitle);
          var alabel = document.createElement("span");
          alabel.className = "block text-body-sm text-muted-foreground";
          alabel.textContent = "Article";
          ainfo.appendChild(alabel);
          link.appendChild(ainfo);
          searchResults.appendChild(link);
        });
      }
      var all = document.createElement("a");
      all.href =
        searchForm.getAttribute("action") + "?q=" + encodeURIComponent(query);
      all.className =
        "mt-1 block px-2 py-2 text-body-sm font-medium underline";
      all.textContent = "See all results for “" + query + "”";
      searchResults.appendChild(all);
      searchResults.classList.remove("hidden");
      setChipsVisible(false);
    };

    var runSearch = function (query) {
      query = query.trim();
      if (query.length < 2) {
        searchResults.classList.add("hidden");
        searchResults.innerHTML = "";
        setChipsVisible(true);
        return;
      }
      var seq = ++searchSeq;
      fetch(
        "/search/suggest.json?q=" +
          encodeURIComponent(query) +
          "&resources[type]=product,article&resources[limit]=8&resources[options][unavailable_products]=hide",
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (seq !== searchSeq) return;
          var results = (data.resources && data.resources.results) || {};
          var products = results.products || [];
          var articles = results.articles || [];
          if (products.length || articles.length) {
            renderSearchProducts(products, articles, query);
          } else {
            renderNoResults(query);
          }
        })
        .catch(function () {
          if (seq === searchSeq) {
            renderSearchMessage("Search isn’t available right now.");
          }
        });
    };

    searchInput.addEventListener("input", function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        runSearch(searchInput.value);
      }, 250);
    });

    searchForm.addEventListener("submit", function (event) {
      event.preventDefault();
      clearTimeout(searchTimer);
      runSearch(searchInput.value);
    });

    document
      .querySelectorAll("[data-spark-search-suggest]")
      .forEach(function (chip) {
        chip.addEventListener("click", function () {
          searchInput.value = chip.getAttribute("data-spark-search-suggest");
          searchInput.focus();
          clearTimeout(searchTimer);
          runSearch(searchInput.value);
        });
      });
  }

  /* ------------------------------------------------------------------
     Hero: rotate the headline tail every 5 s
     ------------------------------------------------------------------ */
  var tails = document.querySelectorAll("[data-spark-hero-tail]");
  if (tails.length > 1 && !prefersReducedMotion) {
    var tailIndex = 0;
    setInterval(function () {
      tails[tailIndex].classList.remove("is-active");
      tails[tailIndex].setAttribute("aria-hidden", "true");
      tailIndex = (tailIndex + 1) % tails.length;
      tails[tailIndex].classList.add("is-active");
      tails[tailIndex].removeAttribute("aria-hidden");
    }, 5000);
  }

  /* ------------------------------------------------------------------
     Quiz: two questions -> recommendation
     Recommendation copy lives in a JSON block rendered by Liquid
     (snippets/spark-quiz.liquid), keyed "who|need".
     ------------------------------------------------------------------ */
  var quiz = document.querySelector("[data-spark-quiz]");
  if (quiz) {
    var recs = {};
    try {
      var recsEl = quiz.querySelector("[data-spark-quiz-recs]");
      if (recsEl) recs = JSON.parse(recsEl.textContent);
    } catch (err) {
      // Malformed copy in the JSON block: the quiz can't recommend, but
      // nothing else on the page should die with it.
    }
    var state = { who: null, need: null, audience: null };

    /* Survey capture — persist every completed fit test three ways, mirroring
       the trial-LP fit test (assets/trial-lp.js):
         1. Cart attributes -> ride the cart onto the order ("Additional
            details" in Shopify admin) for shoppers who buy. First-party order
            context, so NOT consent-gated.
         2. Klaviyo "Fit Test Completed" -> records the response for EVERYONE,
            incl. non-buyers, and ties it to the profile / eventual purchase.
         3. GA4 "fit_test_completed" via window.gtag, plus a dataLayer push.
            The gtag call is the one that lands: there is NO GTM container on
            the storefront (GA4 arrives as gtag.js through the Littledata app
            embed), so nothing consumes the dataLayer — it is kept only for any
            future listener. Same idiom as assets/store-locator.js, whose
            store_locator_* events reach the property today.
       (2) and (3) are third-party marketing/analytics, so they are gated behind
       the same CIPA consent check the trial LP uses. Everything is
       fire-and-forget and wrapped so a failure can never touch the quiz UI. */
    var whoLabels = { her: "Her", him: "Him" };
    var needLabels = {
      overnight: "Overnight",
      maximum: "Maximum daytime",
      light: "Light to moderate",
      boost: "An extra boost",
    };
    var lastRecorded = null;

    /* The funnel before the result (2026-10-05). Until now the quiz sent one
       event, at the result, so a shopper who quit on question two and one who
       never opened the modal were the same row, and there was no completion
       rate to optimise. Four events fill that in, GA4 only, behind the same
       consent gate as the completion above:
         fit_test_opened         which trigger opened it (quiz_trigger)
         fit_test_answered       every tap (quiz_step, quiz_answer)
         fit_test_abandoned      closed before any result (quiz_last_step)
         fit_test_result_clicked what she did with the result (quiz_action:
                                 product, pair, start_over, back, close)
       The quiz_ parameters are registered as GA4 dimensions on property
       335554542; keep the names in step with that if they ever change. */
    var run = { trigger: "", step: "who", sawResult: false, acted: false, product: "" };
    var STEP_NAMES = { q1: "who", q1b: "who_wearer", q2: "need", result: "result" };
    var quizEvent = function (name, props) {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.userCanBeTracked === "function" && !cp.userCanBeTracked()) return;
      var p = { source: "site_quiz", quiz_trigger: run.trigger };
      for (var key in props) {
        if (Object.prototype.hasOwnProperty.call(props, key)) p[key] = props[key];
      }
      try {
        if (typeof window.gtag === "function") window.gtag("event", name, p);
      } catch (e) {}
    };
    // Which of the ~10 triggers opened it, named from the nearest spark-/calm-
    // class around the tapped element (spark-meganav__feat -> "meganav"), so a
    // new trigger reports itself without touching any Liquid. No tap at all
    // means the /#quiz deep link opened it.
    var triggerName = function (el) {
      for (var node = el; node && node !== document.body; node = node.parentElement) {
        var classes = ((node.getAttribute && node.getAttribute("class")) || "").split(/\s+/);
        for (var i = 0; i < classes.length; i++) {
          var c = classes[i];
          if (/^(spark|calm)-/.test(c) && !/^(spark|calm)-btn/.test(c)) {
            var n = c.replace(/^(spark|calm)-/, "").split("__")[0];
            n = { quiz: "home_card", "guide-cta": "collection_guide" }[n] || n;
            return n.replace(/-/g, "_");
          }
        }
      }
      return "other";
    };
    var opener = null;
    document.addEventListener(
      "click",
      function (event) {
        var t = event.target && event.target.closest ? event.target.closest("[data-spark-quiz-modal-open]") : null;
        if (t) opener = t;
      },
      true,
    );
    // Open and close are watched on the modal itself rather than wired into
    // bindOverlay, which every other overlay shares.
    var quizModal = quiz.closest("[data-spark-quiz-modal]");
    if (quizModal && typeof MutationObserver === "function") {
      var quizWasOpen = quizModal.classList.contains("is-open");
      new MutationObserver(function () {
        var isOpen = quizModal.classList.contains("is-open");
        if (isOpen === quizWasOpen) return;
        quizWasOpen = isOpen;
        try {
          if (isOpen) {
            run.trigger = opener ? triggerName(opener) : "link";
            opener = null;
            run.sawResult = run.step === "result";
            run.acted = false;
            quizEvent("fit_test_opened", { quiz_step: run.step });
          } else if (!run.sawResult) {
            quizEvent("fit_test_abandoned", { quiz_last_step: run.step });
          } else if (!run.acted) {
            quizEvent("fit_test_result_clicked", { quiz_action: "close", recommended_product: run.product });
          }
        } catch (e) {}
      }).observe(quizModal, { attributes: true, attributeFilter: ["class"] });
    }
    var resultAction = function (action) {
      run.acted = true;
      quizEvent("fit_test_result_clicked", { quiz_action: action, recommended_product: run.product });
    };
    var resultLink = quiz.querySelector("[data-spark-quiz-result-link]");
    if (resultLink) resultLink.addEventListener("click", function () { resultAction("product"); });
    var pairLinkEl = quiz.querySelector("[data-spark-quiz-pair-link]");
    if (pairLinkEl) pairLinkEl.addEventListener("click", function () { resultAction("pair"); });

    var recordCompletion = function (rec) {
      var key = state.who + "|" + state.need + "|" + state.audience;
      if (key === lastRecorded) return; // guard an identical re-fire
      lastRecorded = key;

      // 1. Cart attributes — first-party order context, not consent-gated.
      try {
        fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: {
              "Fit test — Wearer": whoLabels[state.who] || state.who || "",
              "Fit test — Shopping for":
                state.audience === "loved_one" ? "A loved one" : "Themselves",
              "Fit test — Protection level":
                needLabels[state.need] || state.need || "",
              "Fit test — Recommended": rec.name || "",
              // The companion is recorded too — a shopper who bought only the
              // primary is a different signal from one who was never offered a
              // pairing, and only this tells the two apart.
              "Fit test — Also suggested": (rec.with && rec.with.name) || "",
            },
          }),
          keepalive: true,
        }).catch(function () {});
      } catch (e) {}

      // 2 + 3. Third-party — drop both if the visitor explicitly can't be
      // tracked (same gate the trial LP applies to its Klaviyo/dataLayer push).
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (
        cp &&
        typeof cp.userCanBeTracked === "function" &&
        !cp.userCanBeTracked()
      ) {
        return;
      }
      var props = {
        gender: state.who || "",
        need: state.need || "",
        audience: state.audience || "",
        recommended_product: rec.name || "",
        recommended_url: rec.url || "",
        recommended_pair: (rec.with && rec.with.name) || "",
        source: "site_quiz",
        quiz_trigger: run.trigger,
      };
      try {
        window.klaviyo.push(["track", "Fit Test Completed", props]);
      } catch (e) {}
      try {
        if (typeof window.gtag === "function") {
          // User properties, so "Overnight quiz takers who didn't buy" can be
          // an audience; registered as user-scoped dimensions alongside the rest.
          window.gtag("set", "user_properties", {
            fit_need: props.need,
            fit_gender: props.gender,
            fit_audience: props.audience,
          });
          window.gtag("event", "fit_test_completed", props);
        }
      } catch (e) {}
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "fit_test_completed",
          gender: props.gender,
          need: props.need,
          audience: props.audience,
          recommended_product: props.recommended_product,
          recommended_url: props.recommended_url,
          recommended_pair: props.recommended_pair,
          source: props.source,
          quiz_trigger: props.quiz_trigger,
        });
      } catch (e) {}
    };

    var showPanel = function (name) {
      run.step = STEP_NAMES[name] || name;
      if (name === "result") run.sawResult = true;
      quiz
        .querySelectorAll("[data-spark-quiz-panel]")
        .forEach(function (panel) {
          panel.classList.toggle(
            "is-active",
            panel.getAttribute("data-spark-quiz-panel") === name,
          );
        });
    };

    var renderResult = function () {
      var rec = recs[state.who + "|" + state.need];
      if (!rec) return;
      var img = quiz.querySelector("[data-spark-quiz-result-img]");
      img.src = rec.img;
      img.alt = rec.name;
      quiz.querySelector("[data-spark-quiz-result-name]").textContent =
        rec.name;
      quiz.querySelector("[data-spark-quiz-result-price]").textContent =
        rec.price;
      quiz.querySelector("[data-spark-quiz-result-why]").textContent = rec.why;
      quiz.querySelector("[data-spark-quiz-result-link]").href = rec.url;
      run.product = rec.name || "";

      /* The companion — the second half of the routine (see the panel's own
         comment in snippets/spark-quiz-modal.liquid). Hidden outright when an
         outcome has none, and its image hidden when the product's featured
         image didn't resolve, so the row never draws a broken thumbnail. */
      var pairEl = quiz.querySelector("[data-spark-quiz-pair]");
      var pair = rec.with;
      if (pairEl) {
        if (pair && pair.name && pair.url) {
          quiz.querySelector("[data-spark-quiz-pair-name]").textContent =
            pair.name;
          quiz.querySelector("[data-spark-quiz-pair-price]").textContent =
            pair.price || "";
          quiz.querySelector("[data-spark-quiz-pair-why]").textContent =
            pair.why || "";
          var pairLink = quiz.querySelector("[data-spark-quiz-pair-link]");
          pairLink.href = pair.url;
          pairLink.setAttribute("aria-label", "Shop " + pair.name);
          var pairImg = quiz.querySelector("[data-spark-quiz-pair-img]");
          if (pair.img) {
            pairImg.src = pair.img;
            pairImg.hidden = false;
          } else {
            pairImg.removeAttribute("src");
            pairImg.hidden = true;
          }
          pairEl.hidden = false;
        } else {
          pairEl.hidden = true;
        }
      }

      showPanel("result");
      recordCompletion(rec);
    };

    quiz.querySelectorAll("[data-spark-quiz-who]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var who = btn.getAttribute("data-spark-quiz-who");
        quizEvent("fit_test_answered", { quiz_step: run.step, quiz_answer: who === "loved" ? "loved_one" : who });
        if (who === "loved") {
          // Caregiver path: remember it, then ask who actually wears it (q1b,
          // whose own her/him buttons run through this same handler). Keeping
          // the audience signal lets us tell caregivers from the wearer.
          state.audience = "loved_one";
          showPanel("q1b");
          return;
        }
        state.who = who;
        // Only default to "self" when audience is unset — the q1b her/him
        // buttons reuse this handler, so we must NOT clobber a "loved_one" set
        // on the way in. Invariant: audience is cleared on every path back to
        // q1 (see the goto handler) and q1b never offers a "loved" option, so
        // this guard is the sole thing keeping caregivers from being mislabeled
        // "self" — keep it that way if q1b ever changes.
        if (!state.audience) state.audience = "self";
        showPanel("q2");
      });
    });

    quiz.querySelectorAll("[data-spark-quiz-need]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        state.need = btn.getAttribute("data-spark-quiz-need");
        quizEvent("fit_test_answered", { quiz_step: "need", quiz_answer: state.need });
        renderResult();
      });
    });

    quiz.querySelectorAll("[data-spark-quiz-goto]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var target = btn.getAttribute("data-spark-quiz-goto");
        if (run.step === "result") {
          resultAction(target === "q1" ? "start_over" : "back");
          // Start over begins a new run: quitting it mid-way is an abandonment
          // again, even though this open already showed a result.
          if (target === "q1") {
            run.sawResult = false;
            run.acted = false;
          }
        }
        if (target === "q1") {
          // Back to the start / "Start over": clear who + audience and the
          // dedupe guard so a fresh run records even if it lands on the same
          // recommendation.
          state.who = null;
          state.audience = null;
          lastRecorded = null;
        }
        if (target === "q2") state.need = null;
        showPanel(target);
      });
    });
  }

  /* ------------------------------------------------------------------
     "Know the difference" cards -> gender chooser.
     Each type card (Overnight / Maximum / Pads & guards) is an <a> whose
     href is a no-JS fallback (the gender-neutral type collection) and
     which carries data-shop-women / data-shop-men gendered collection
     URLs. Clicking one opens the small chooser popover
     (snippets/spark-diff-chooser.liquid), remembers the card, and on a
     choice navigates to the matching URL. The Boosters card instead
     carries data-shop-direct and no data-spark-diff-card, so it is a
     plain link that navigates on its own — no chooser.

     Mirrors the shared overlay pattern (focus moves in on open, is
     trapped, Esc / backdrop close, focus returns to the card), but
     toggles Tailwind `hidden`/`flex` directly rather than the .is-open
     class, so no bespoke CSS is needed.
     ------------------------------------------------------------------ */
  var diffChooser = document.querySelector("[data-spark-diff-chooser]");
  var diffCards = document.querySelectorAll("[data-spark-diff-card]");
  if (diffChooser && diffCards.length) {
    var diffNameEl = diffChooser.querySelector("[data-spark-diff-chooser-name]");
    var diffUrls = { women: null, men: null };
    var diffLastCard = null;

    var diffSetOpen = function (open) {
      diffChooser.classList.toggle("hidden", !open);
      diffChooser.classList.toggle("flex", open);
      if (open) {
        var focusTarget = diffChooser.querySelector(
          "[data-spark-diff-chooser-choice]",
        );
        if (focusTarget) focusTarget.focus();
      } else if (diffLastCard) {
        diffLastCard.focus();
      }
    };

    diffCards.forEach(function (card) {
      card.addEventListener("click", function (event) {
        event.preventDefault(); // the href is only the no-JS fallback
        diffUrls.women = card.getAttribute("data-shop-women");
        diffUrls.men = card.getAttribute("data-shop-men");
        if (diffNameEl) {
          diffNameEl.textContent =
            card.getAttribute("data-shop-name") || "this protection";
        }
        diffLastCard = card;
        diffSetOpen(true);
      });
    });

    diffChooser
      .querySelectorAll("[data-spark-diff-chooser-choice]")
      .forEach(function (btn) {
        btn.addEventListener("click", function () {
          var url = diffUrls[btn.getAttribute("data-spark-diff-chooser-choice")];
          if (url) window.location.href = url;
        });
      });

    diffChooser
      .querySelectorAll("[data-spark-diff-chooser-close]")
      .forEach(function (el) {
        el.addEventListener("click", function () {
          diffSetOpen(false);
        });
      });

    // Backdrop click (the overlay itself, not the panel) closes.
    diffChooser.addEventListener("click", function (event) {
      if (event.target === diffChooser) diffSetOpen(false);
    });

    // Escape closes; Tab is trapped inside the open chooser.
    document.addEventListener("keydown", function (event) {
      if (diffChooser.classList.contains("hidden")) return;
      if (event.key === "Escape") {
        diffSetOpen(false);
        return;
      }
      if (event.key === "Tab") {
        var focusables = diffChooser.querySelectorAll(
          'a[href], button, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ------------------------------------------------------------------
     Category nav (homepage bestsellers): a For women / For men toggle
     swaps which category group is shown. Each [data-spark-catnav-btn]
     matches a [data-spark-catnav-group] in the same section; the chosen
     group is shown, the others carry the boolean `hidden`. Exactly one
     button is aria-pressed. Progressive enhancement: without JS every
     group shows and the buttons are inert.
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     Load more: reveal the collapsed items in a [data-spark-loadmore]
     block on button click, then retire the button. Used by the reviews
     wall. With JS off the no-js CSS shows every item and hides the
     button, so nothing is trapped.
     ------------------------------------------------------------------ */
  document.querySelectorAll("[data-spark-loadmore]").forEach(function (root) {
    var button = root.querySelector("[data-spark-loadmore-btn]");
    var items = root.querySelectorAll("[data-spark-loadmore-item]");
    if (!button || !items.length) {
      if (button) button.hidden = true;
      return;
    }
    button.addEventListener("click", function () {
      items.forEach(function (item) {
        item.classList.remove("spark-loadmore__hidden");
      });
      button.hidden = true;
    });
  });

  document.querySelectorAll("[data-spark-catnav]").forEach(function (nav) {
    var buttons = nav.querySelectorAll("[data-spark-catnav-btn]");
    var scope = nav.closest("section") || document;
    var groups = scope.querySelectorAll("[data-spark-catnav-group]");
    if (!buttons.length || !groups.length) return;

    var applyCatnav = function (key) {
      buttons.forEach(function (btn) {
        btn.setAttribute(
          "aria-pressed",
          btn.getAttribute("data-spark-catnav-btn") === key ? "true" : "false",
        );
      });
      groups.forEach(function (group) {
        group.hidden = group.getAttribute("data-spark-catnav-group") !== key;
      });
    };

    buttons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        applyCatnav(btn.getAttribute("data-spark-catnav-btn"));
      });
    });
  });

  /* ------------------------------------------------------------------
     Reviews: filter chips narrow the review grid by category. Exactly one
     chip is aria-pressed at a time ("all" to start); clicking a chip shows
     the cards whose data-category matches (or every card for "all") and
     hides the rest via the boolean `hidden` property. Progressive
     enhancement: without JS every card shows and the chips are inert.
     ------------------------------------------------------------------ */
  var reviewFilters = document.querySelectorAll("[data-spark-reviews-filter]");
  if (reviewFilters.length) {
    var reviewCards = document.querySelectorAll("[data-spark-review]");

    var applyReviewFilter = function (key) {
      reviewFilters.forEach(function (chip) {
        chip.setAttribute(
          "aria-pressed",
          chip.getAttribute("data-spark-reviews-filter") === key
            ? "true"
            : "false",
        );
      });
      reviewCards.forEach(function (card) {
        card.hidden =
          key !== "all" && card.getAttribute("data-category") !== key;
      });
    };

    reviewFilters.forEach(function (chip) {
      chip.addEventListener("click", function () {
        applyReviewFilter(chip.getAttribute("data-spark-reviews-filter"));
      });
    });
  }

  /* ------------------------------------------------------------------
     Video renditions — pick ONE source, load it ONCE. Ambient videos carry
     no eager `src`; both encodes live in data-src-sd (small) and
     data-src-hd (master). Setting the chosen source here — before any load —
     means the browser fetches a single file and autoplay starts it without
     the reload-and-stall (and Safari "paused" overlay) that a late src swap
     on an already-playing element causes. Phones / Save-Data / 2g-3g get SD;
     a wide screen on a good connection gets HD.
     ------------------------------------------------------------------ */
  var wantsLightVideo = window.matchMedia("(max-width: 820px)").matches;
  try {
    var conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (
      conn &&
      (conn.saveData === true ||
        /(^|-)(slow-2g|2g|3g)$/.test(conn.effectiveType || ""))
    ) {
      wantsLightVideo = true;
    }
  } catch (e) {
    /* Network Information API unavailable — viewport check stands. */
  }
  var useHdVideo = !wantsLightVideo;

  /* Low Power Mode (and any autoplay-policy block) stops muted autoplay and
     makes Safari paint a play/pause glyph over the frozen frame. We can't
     force playback — it's a user setting — so we degrade cleanly: swap the
     <video> for its poster still (or just hide it, letting the section's own
     gradient/background show) so no control glyph is ever shown. */
  var posterFallback = function (video) {
    if (video.dataset.sparkFallback) return;
    video.dataset.sparkFallback = "1";
    var poster = video.getAttribute("poster");
    if (poster) {
      var img = document.createElement("img");
      img.src = poster;
      img.alt = "";
      img.setAttribute("aria-hidden", "true");
      img.className = video.className;
      if (video.parentNode) video.parentNode.insertBefore(img, video);
    }
    video.style.display = "none";
  };

  /* Static-image backstop for the loading window. The `poster` attribute
     covers a video before its first frame decodes, but browsers (notably
     iOS/Safari) drop the poster the moment JS assigns a new `src` and calls
     load() — leaving a transparent/black box while the source buffers. So we
     also paint the SAME poster URL as a cover background on the <video>
     itself: one deduplicated fetch, but the still frame now persists through
     the src swap and any buffering/error state, and is hidden the instant the
     opaque video frames draw over it. No poster (e.g. hover videos) → skip. */
  var paintPosterBackground = function (video) {
    var poster = video.getAttribute("poster");
    if (!poster || video.style.backgroundImage) return;
    video.style.backgroundImage = 'url("' + poster + '")';
    video.style.backgroundSize = "cover";
    video.style.backgroundPosition = "center";
    video.style.backgroundRepeat = "no-repeat";
  };

  var loadVideoSource = function (video) {
    if (video.dataset.sparkSrcSet) return;
    if (video.dataset.sparkFallback) return; // reduced motion / autoplay blocked
    // Backstop the still frame before the src swap can drop the native poster.
    paintPosterBackground(video);
    var sd = video.getAttribute("data-src-sd");
    var hd = video.getAttribute("data-src-hd");
    var chosen = useHdVideo && hd ? hd : sd || hd;
    if (!chosen || video.getAttribute("src") === chosen) return;
    video.dataset.sparkSrcSet = "1";
    video.src = chosen;
    try {
      video.load();
    } catch (e) {}
  };

  /* IMAGES FIRST. Every one of these sources used to be assigned right here,
     at parse time — so a homepage with five ambient videos opened five MP4
     fetches that raced the hero poster, the product photography and the logo
     strips for bandwidth and connections. The video is decoration; the imagery
     is the content, and on a slow line the difference is what the shopper sees
     for the first few seconds.

     So each source is now assigned only when BOTH are true:
       1. window.load has fired — every image in the initial page has finished,
          so no video byte can ever queue ahead of one; and
       2. the video is within 300px of the viewport — offscreen sections cost
          nothing at all until they are approached.
     Posters still paint immediately (they are plain <img>-equivalent fetches),
     so each section shows its still frame and then quietly starts moving. */
  var whenPageLoaded = function (fn) {
    if (document.readyState === "complete") {
      fn();
      return;
    }
    window.addEventListener("load", fn, { once: true });
  };

  whenPageLoaded(function () {
    var sourced = document.querySelectorAll(
      "video[data-src-sd], video[data-src-hd]",
    );
    if (!("IntersectionObserver" in window)) {
      sourced.forEach(loadVideoSource);
      return;
    }
    var sourceObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadVideoSource(entry.target);
          sourceObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "300px 0px" },
    );
    sourced.forEach(function (video) {
      sourceObserver.observe(video);
    });
  });

  /* ------------------------------------------------------------------
     Ambient videos: force muted inline autoplay (Safari needs the
     property set from JS, not just the attribute). Offscreen videos are
     paused via IntersectionObserver so six loops don't decode at once;
     reduced-motion users get static posters.
     ------------------------------------------------------------------ */
  var ambientVideos = document.querySelectorAll("[data-spark-video]");
  ambientVideos.forEach(function (video) {
    video.muted = true;
    video.defaultMuted = true;
    paintPosterBackground(video);
    if (prefersReducedMotion) {
      video.removeAttribute("autoplay");
      video.pause();
      posterFallback(video);
    }
  });

  if (!prefersReducedMotion) {
    var playVideo = function (video) {
      if (video.dataset.sparkFallback) return;
      var p = video.play();
      if (p && p.catch) {
        p.catch(function (err) {
          /* NotAllowedError = autoplay blocked (e.g. Low Power Mode) — show
             the poster instead of a frozen video with a play glyph. Ignore
             AbortError, which just means the offscreen observer paused it. */
          if (err && err.name === "NotAllowedError") posterFallback(video);
        });
      }
    };
    if ("IntersectionObserver" in window) {
      var videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) playVideo(entry.target);
            else entry.target.pause();
          });
        },
        { rootMargin: "200px 0px" },
      );
      ambientVideos.forEach(function (video) {
        videoObserver.observe(video);
      });
    } else {
      ambientVideos.forEach(playVideo);
    }
  }

  /* ------------------------------------------------------------------
     "Made in the USA" foil seal: mouse-driven 3D tilt. The iridescent
     shimmer is pure CSS (spark-tw/mechanics.css); this only adds the
     parallax tilt, and only on hover-capable pointers and when motion is
     allowed (perspective lives on .spark-usa-seal).
     ------------------------------------------------------------------ */
  if (!prefersReducedMotion && window.matchMedia("(hover: hover)").matches) {
    document.querySelectorAll("[data-spark-usa-tilt]").forEach(function (seal) {
      var sealInner = seal.querySelector(".spark-usa-seal__inner");
      if (!sealInner) return;
      seal.addEventListener("mousemove", function (e) {
        var r = seal.getBoundingClientRect();
        var ry = ((e.clientX - r.left) / r.width - 0.5) * 20;
        var rx = (0.5 - (e.clientY - r.top) / r.height) * 15;
        sealInner.style.transform =
          "rotateX(" + rx.toFixed(2) + "deg) rotateY(" + ry.toFixed(2) + "deg) scale(1.04)";
      });
      seal.addEventListener("mouseleave", function () {
        sealInner.style.transform = "rotateX(0deg) rotateY(0deg) scale(1)";
      });
    });
  }

  /* ------------------------------------------------------------------
     Carousels: a horizontal snap track scrolled one card at a time by
     prev/next buttons; the buttons disable at each end. Reusable —
     every [data-spark-carousel] on the page is wired independently, and
     missing parts are tolerated so partial markup never throws. Manual
     swipe/scroll still works; the buttons just nudge the same track.
     ------------------------------------------------------------------ */
  document.querySelectorAll("[data-spark-carousel]").forEach(function (root) {
    var track = root.querySelector("[data-spark-carousel-track]");
    if (!track) return;
    var prev = root.querySelector("[data-spark-carousel-prev]");
    var next = root.querySelector("[data-spark-carousel-next]");
    var step = function () {
      var card = track.firstElementChild;
      return card
        ? card.getBoundingClientRect().width + 18
        : track.clientWidth * 0.8;
    };
    var update = function () {
      var max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    if (prev) {
      prev.addEventListener("click", function () {
        track.scrollBy({ left: -step(), behavior: "smooth" });
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        track.scrollBy({ left: step(), behavior: "smooth" });
      });
    }
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
  });

  /* ----- Store finder ----- */
  /* The store-locator page's finder lives in its own asset (spark-store-map.js):
     an interactive Leaflet + OpenStreetMap map that plots every nearby Walmart /
     CVS / Target / Walgreens at once from a pre-baked local dataset
     (spark-store-data.js), so the shopper never picks a chain. It's loaded only
     on templates/page.store-locator.liquid. */

  /* ----- Collection guide: the category rail pins once the cards scroll away -----
     The page opens with a row of picture cards, one per product type. They are the
     right thing at the top — a shopper who doesn't know underwear from pads needs
     to see them — but they are five cards deep and gone by the time she is reading
     a grid, so changing her mind meant scrolling back to the top of a long page.

     This pins the same choices, as names, under the header: shown the moment the
     card row leaves the top of the viewport, hidden again when it comes back, with
     the pill for whichever group she is reading marked aria-current. The pills are
     plain in-page anchors, so a click needs none of this. */
  document.querySelectorAll("[data-spark-guide-nav]").forEach(function (nav) {
    var cards = document.querySelector("[data-spark-guide-cards]");
    var list = nav.querySelector("[data-spark-guide-nav-list]");
    var links = [].slice.call(nav.querySelectorAll("[data-spark-guide-nav-link]"));
    if (!cards || !links.length || !("IntersectionObserver" in window)) return;

    /* Publish the bar's real height so the anchors can clear it. Hardcoding it
       put the landing 1px under the bar — the pill is 44px but the bar is 65
       with its padding and rule, and it changes again if the type scale does.
       guide.css reads this in the scroll-margin calc. */
    var publishHeight = function () {
      document.documentElement.style.setProperty(
        "--spark-guide-nav-h",
        nav.offsetHeight + "px"
      );
    };
    publishHeight();
    window.addEventListener("resize", publishHeight);

    /* Pin/unpin. Same test as the PDP's sticky bar: not intersecting AND above
       the fold, so the bar never appears while the shopper is still ABOVE the
       cards (scrolled up past them, or on a page that opens mid-document). */
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          nav.classList.toggle("is-visible", passed);
        });
      },
      { threshold: 0 }
    ).observe(cards);

    /* Scroll spy. Each pill's href IS the id of the group it belongs to, so the
       map needs no separate data attribute to fall out of sync. */
    var targets = [];
    links.forEach(function (link) {
      var id = (link.getAttribute("href") || "").replace(/^#/, "");
      var el = id && document.getElementById(id);
      if (el) targets.push({ link: link, el: el });
    });
    if (!targets.length) return;

    var current = null;
    var setCurrent = function (link) {
      if (link === current) return;
      current = link;
      links.forEach(function (l) {
        if (l === link) l.setAttribute("aria-current", "true");
        else l.removeAttribute("aria-current");
      });
      /* Keep the marked pill where she can see it. scrollLeft on the list, NOT
         scrollIntoView: that would also scroll the PAGE to reveal a pill inside a
         fixed bar, which on a phone means the document jumping while she reads. */
      if (list && list.scrollWidth > list.clientWidth) {
        list.scrollTo({
          left: Math.max(0, link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2),
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      }
    };

    /* A group counts as "the one being read" once its top has crossed just under
       the bar, and stops counting once it has left through the top. The bottom
       margin keeps the LAST group from stealing the mark the instant it appears
       at the foot of the viewport. */
    var offset = 56 + 44 + 8;
    var spy = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          targets.forEach(function (t) {
            /* The absorbency filter above can display:none a whole group; a
               hidden group must not claim the rail. */
            if (t.el === entry.target && entry.target.offsetParent !== null) setCurrent(t.link);
          });
        });
      },
      { rootMargin: "-" + offset + "px 0px -60% 0px", threshold: 0 }
    );
    targets.forEach(function (t) {
      spy.observe(t.el);
    });
  });

  /* ----- Collection guide: absorbency filter over the by-type product grids -----

     IT RENDERS IN PLACE. It used to scrollIntoView() the first surviving group on
     every tap, which carried the shopper away from the control she had just used.
     Nothing here moves the document.

     ONE FILTER, TWO PLACES TO TAP IT. The cup scale under the chooser and the three
     level chips in the pinned category rail are the same [data-spark-absfilter-btn]
     controls, so the buttons are gathered from the whole document (the rail is
     outside the wrapper) and every one of them shows the same pressed state.
     Tapping the level that is already pressed goes back to all products. */
  document.querySelectorAll("[data-spark-absfilter]").forEach(function (root) {
    var btns = document.querySelectorAll("[data-spark-absfilter-btn]");
    var cards = root.querySelectorAll("[data-abs]");
    var groups = root.querySelectorAll("[data-abs-group]");
    if (!btns.length || !cards.length) return;
    var current = "all";
    var status = root.querySelector("[data-spark-absfilter-status]");
    var summary = root.querySelector("[data-spark-absfilter-summary]");
    var announce = false;
    function apply(filter) {
      current = filter;
      cards.forEach(function (card) {
        var list = (card.getAttribute("data-abs") || "").split(" ");
        var show = filter === "all" || list.indexOf(filter) !== -1;
        card.style.display = show ? "" : "none";
      });
      groups.forEach(function (group) {
        var any = false;
        group.querySelectorAll("[data-abs]").forEach(function (c) {
          if (c.style.display !== "none") any = true;
        });
        group.style.display = any ? "" : "none";
      });
      btns.forEach(function (b) {
        b.setAttribute("aria-pressed", b.getAttribute("data-spark-absfilter-btn") === filter ? "true" : "false");
      });
      /* The results line above the groups: total, then one chip per group that still
         has cards, with its count. Hidden when nothing is filtered. */
      if (summary) {
        if (filter === "all") {
          summary.classList.add("hidden");
        } else {
          var total = 0;
          summary.querySelectorAll("[data-summary-group]").forEach(function (li) {
            var g = document.getElementById(li.getAttribute("data-summary-group"));
            var n = 0;
            if (g) g.querySelectorAll("[data-abs]").forEach(function (c) { if (c.style.display !== "none") n++; });
            total += n;
            li.style.display = n ? "" : "none";
            var cnt = li.querySelector("[data-summary-count]");
            if (cnt) cnt.textContent = n;
          });
          var lvl = filter.charAt(0).toUpperCase() + filter.slice(1);
          summary.querySelector("[data-summary-total]").textContent =
            total + " " + lvl + " product" + (total === 1 ? "" : "s") + (total ? ":" : "");
          summary.classList.remove("hidden");
        }
      }
      /* Say what changed (screen readers only — on a phone the grids are a screen
         below the stacks, so the tap is otherwise silent). Silent on page load. */
      if (status && announce) {
        var shown = 0;
        cards.forEach(function (c) { if (c.style.display !== "none") shown++; });
        status.textContent = filter === "all"
          ? "Showing all " + shown + " products"
          : "Showing " + shown + " " + filter + " products";
      }
    }
    btns.forEach(function (b) {
      b.addEventListener("click", function () {
        var key = b.getAttribute("data-spark-absfilter-btn");
        announce = true;
        apply(key !== "all" && key === current ? "all" : key);
        /* From the pinned rail the shopper is somewhere down the grids, and
           hiding groups above her moves the page under her. Bring the first
           group that is still showing up under the rail — the control itself is
           pinned, so it does not leave the screen. From the cup scale, nothing
           moves: the scale sits directly above the grids. */
        if (!root.contains(b)) {
          var first = null;
          groups.forEach(function (g) {
            if (!first && g.style.display !== "none") first = g;
          });
          if (first) first.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
        }
      });
    });
    /* A category pill whose group the filter has hidden would go nowhere: show
       everything again first, then let the link do its jump. */
    document.querySelectorAll("[data-spark-guide-nav-link]").forEach(function (link) {
      link.addEventListener("click", function () {
        var target = document.querySelector(link.getAttribute("href"));
        if (target && target.style.display === "none") apply("all");
      });
    });
    /* ?level=overnight|maximum|moderate opens the page with that level pressed:
       the homepage finder's cup stacks link here (snippets/spark-abs-filter, href). */
    var initial = "all";
    try {
      var lvlParam = new URLSearchParams(window.location.search).get("level");
      btns.forEach(function (b) {
        if (lvlParam && b.getAttribute("data-spark-absfilter-btn") === lvlParam) initial = lvlParam;
      });
    } catch (e) {}
    apply(initial);
  });

  /* The rail's right-edge fade (guide.css .is-clipped): on while the list is wider
     than the bar and not scrolled to its end. */
  document.querySelectorAll("[data-spark-guide-nav-list]").forEach(function (list) {
    function clip() {
      var more = list.scrollWidth - list.clientWidth - list.scrollLeft > 4;
      list.classList.toggle("is-clipped", more);
    }
    list.addEventListener("scroll", clip, { passive: true });
    window.addEventListener("resize", clip);
    clip();
  });

  /* ----- Native dialogs: [data-spark-dialog-open="id"] opens <dialog id>, any
     [data-spark-dialog-close] inside closes it, and so does a click on the backdrop
     (a click whose target is the dialog element itself). Esc and focus trapping come
     with the element. Used by the credentials band's "Meet our urologist". ----- */
  document.addEventListener("click", function (e) {
    var opener = e.target.closest && e.target.closest("[data-spark-dialog-open]");
    if (opener) {
      var dlg = document.getElementById(opener.getAttribute("data-spark-dialog-open"));
      if (dlg && typeof dlg.showModal === "function") {
        e.preventDefault();
        dlg.showModal();
      }
      return;
    }
    var closer = e.target.closest && e.target.closest("[data-spark-dialog-close]");
    if (closer) {
      var d = closer.closest("dialog");
      if (d) d.close();
      return;
    }
    /* Backdrop: only our own dialogs, and only a click that lands OUTSIDE the
       dialog's box — other dialogs on the site (the trial page's sheet) pad the
       element itself, and a tap in that padding must not close them. */
    var dl = e.target;
    if (dl.tagName === "DIALOG" && dl.open && dl.classList.contains("spark-dialog")) {
      var r = dl.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dl.close();
    }
  });

  /* ----- Horizontal scrollers with arrow buttons ([data-spark-scroller]): the
     contained review row. Swipe works on its own; the arrows are for everyone who
     doesn't swipe, and they disable themselves at either end. ----- */
  document.querySelectorAll("[data-spark-scroller]").forEach(function (box) {
    var track = box.querySelector("[data-spark-scroller-track]");
    var prev = box.querySelector("[data-spark-scroller-prev]");
    var next = box.querySelector("[data-spark-scroller-next]");
    if (!track) return;
    function step(dir) {
      var card = track.firstElementChild;
      var w = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
      var n = Math.max(1, Math.floor(track.clientWidth / w));
      track.scrollBy({ left: dir * n * w, behavior: prefersReducedMotion ? "auto" : "smooth" });
    }
    function sync() {
      var max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    }
    if (prev) prev.addEventListener("click", function () { step(-1); });
    if (next) next.addEventListener("click", function () { step(1); });
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    sync();
  });

  /* ------------------------------------------------------------------
     Absorbency spectrum (collection guide): fill the glasses when the
     scale scrolls into view. The CSS holds every value; this just adds
     .is-in once. Reduced motion / no IntersectionObserver -> fill
     immediately, so the final state always shows.
     ------------------------------------------------------------------ */
  document.querySelectorAll("[data-spark-absscale]").forEach(function (scale) {
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      scale.classList.add("is-in");
      return;
    }
    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -15% 0px" },
    );
    obs.observe(scale);
  });

  // Reviews strip: on mobile, auto-advance the snap row every 5s so shoppers
  // see more than one review without swiping. Pauses briefly after a manual
  // swipe, skips when reduced motion is requested, and only counts cards that
  // the category filter is currently showing.
  document.querySelectorAll(".spark-reviews__row").forEach(function (row) {
    var mqMobile = window.matchMedia("(max-width: 760px)");
    var mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    var pauseUntil = 0;

    ["pointerdown", "touchstart", "wheel"].forEach(function (ev) {
      row.addEventListener(
        ev,
        function () {
          pauseUntil = Date.now() + 9000;
        },
        { passive: true }
      );
    });

    function visibleCards() {
      return Array.prototype.filter.call(row.children, function (c) {
        return c.offsetParent !== null;
      });
    }

    setInterval(function () {
      if (!mqMobile.matches || mqReduce.matches) return;
      if (Date.now() < pauseUntil) return;
      if (document.hidden) return;
      var list = visibleCards();
      if (list.length < 2) return;
      var rowLeft = row.getBoundingClientRect().left;
      var current = 0;
      var closest = Infinity;
      list.forEach(function (c, i) {
        var d = Math.abs(c.getBoundingClientRect().left - rowLeft);
        if (d < closest) {
          closest = d;
          current = i;
        }
      });
      var target = list[(current + 1) % list.length];
      row.scrollBy({
        left: target.getBoundingClientRect().left - rowLeft,
        behavior: "smooth",
      });
    }, 5000);
  });

  // Featured mega-menu cards: play the product video on hover, reset on leave.
  document.querySelectorAll("[data-spark-hovervid]").forEach(function (v) {
    var host = v.closest(".group") || v.parentElement;
    if (!host) return;
    host.addEventListener("mouseenter", function () {
      // Attach the source on first hover — the markup ships without one so the
      // header costs no video bytes on pages where nobody opens the mega-menu.
      if (!v.getAttribute("src")) {
        var hoverSrc = v.getAttribute("data-hover-src");
        if (!hoverSrc) return;
        v.src = hoverSrc;
      }
      // The source clips are deliberately slow/ambient; speed them up so the
      // hover reads as "something happened" rather than a near-still image.
      try {
        v.playbackRate = 2;
      } catch (e) {}
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
    host.addEventListener("mouseleave", function () {
      v.pause();
      try {
        v.currentTime = 0;
      } catch (e) {}
    });
  });

})();

/* ------------------------------------------------------------------
   Accessibility: host Avada's real trigger in the header, with our own icon
   as the always-present fallback.

   Two lessons are baked in here, both found by reproducing them in a browser:

   1. HOST, don't proxy. Forwarding synthetic clicks meant guessing which of
      Avada's elements was live and when, and it was wrong on real devices: their
      placeholder button only exists on a first visit, and the flag their bundle
      uses to auto-open is captured synchronously at execute time, so no click
      handler can set it in time. A tap on their own element sidesteps all of it.

   2. Avada does not always render a button. With sessionStorage
      sea_a11y_icon_closed set — their "user dismissed the bubble" state — the
      loader creates NOTHING. Hosting alone therefore left the header with no
      accessibility control at all, which is the worst possible failure for this
      particular feature. So our icon ships inside the slot and only steps aside
      once their real button is in it.
   ------------------------------------------------------------------ */
(function () {
  var slot = document.querySelector("[data-spark-a11y-slot]");
  if (!slot) return;
  var fallback = slot.querySelector("[data-spark-a11y-fallback]");

  // Preference order: the persistent widget button their main bundle creates,
  // else the loader's placeholder so the real control appears as early as possible.
  var IDS = ["Sea-Accessibility__ButtonWidget", "Sea-Accessibility__LoaderButton"];

  function findAvada() {
    for (var i = 0; i < IDS.length; i += 1) {
      var el = document.getElementById(IDS[i]);
      if (el) return el;
    }
    return null;
  }

  // Toggled via the `hidden` attribute, NOT style.display: the slot's sizing rule
  // sets display:inline-flex !important on every child, and a stylesheet
  // !important always beats a non-important inline style — so setting
  // fallback.style.display = "none" left both icons on screen. The matching
  // [hidden] rule lives beside that sizing rule in snippets/spark-header.liquid.
  function showFallback(visible) {
    if (!fallback) return;
    // No-op when already in the wanted state. setAttribute queues a mutation
    // record even when the value is unchanged, and the observer below now
    // watches attributes — without this guard, hiding/showing our own icon
    // re-triggers rehome() forever and the page never goes idle.
    if (visible === !fallback.hasAttribute("hidden")) return;
    if (visible) fallback.removeAttribute("hidden");
    else fallback.setAttribute("hidden", "");
  }

  // EXISTS IS NOT ENOUGH. When the visitor has dismissed Avada's bubble, their
  // bundle still builds #Sea-Accessibility__ButtonWidget — display:flex,
  // visibility:visible, 32x32 — and simply sets opacity:0 on it. Hosting that and
  // hiding our own icon left the header with a blank, invisible control: a tap
  // does reach it (opacity does not affect hit-testing) but there is nothing to
  // see or aim at, which reads as "the accessibility button stopped working".
  function isUsable(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    if (parseFloat(cs.opacity) === 0) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  // Opacity:0 does not stop hit-testing. Avada's .Hidden class pairs opacity:0
  // with pointer-events:none, but they also hide the bubble by setting only
  // opacity:0 (inline or otherwise). Handed back to <body>, that node sits
  // fixed at bottom-left with z-index ~2147483647 and steals taps from product
  // cards / CTAs underneath. Kill hit-testing while unusable; restore when we
  // host a real launcher. Programmatic .click() from the fallback still works.
  function setHitTestable(el, on) {
    if (!el) return;
    var next = on ? "" : "none";
    if (el.style.pointerEvents === next) return;
    el.style.pointerEvents = next;
  }

  function rehome() {
    var el = findAvada();
    // Nothing usable to host: show our own icon. Covers both "Avada built
    // nothing" and "Avada built it but is holding it invisible", and the gap
    // between their placeholder being removed and the real widget appended.
    if (!isUsable(el)) {
      // An invisible node parked in the slot would still take 32px of layout,
      // pushing our icon sideways — hand it back to <body>, where their own
      // fixed positioning applies and it stays out of the way.
      if (el && el.parentNode === slot) document.body.appendChild(el);
      setHitTestable(el, false);
      showFallback(true);
      return;
    }
    if (el.parentNode !== slot) {
      // Drop a previously hosted Avada node (typically the placeholder their
      // bundle is about to discard) but never our own fallback button.
      var kids = slot.children;
      for (var i = kids.length - 1; i >= 0; i -= 1) {
        if (kids[i] !== fallback) slot.removeChild(kids[i]);
      }
      slot.appendChild(el);
    }
    setHitTestable(el, true);
    showFallback(false);
  }

  rehome();

  // Keep watching for the page's lifetime: the loader appends a placeholder, then
  // the main bundle REMOVES it and appends its own #Sea-Accessibility__ButtonWidget
  // to <body>. A one-shot move leaves the header holding a discarded element.
  if ("MutationObserver" in window) {
    // Coalesce bursts (Avada rewrites several attributes at once) into one pass.
    var queued = false;
    var scheduleRehome = function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        rehome();
      });
    };
    var mo = new MutationObserver(scheduleRehome);
    // attributes too: Avada reveals/hides its button by flipping opacity in the
    // style attribute, which a childList-only observer never sees, so the header
    // would keep our fallback (or their blank button) after the state changed.
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
  }

  // Fallback path: Avada has rendered nothing, so revive it on demand.
  if (!fallback) return;
  fallback.addEventListener("click", function () {
    // Their button may exist but be held at opacity:0 (dismissed bubble). A
    // programmatic click still opens the panel — verified — so drive it directly
    // rather than waiting for a rebuild that never comes.
    var existing = findAvada();
    if (existing) {
      existing.click();
      return;
    }
    // Clear their "dismissed for this session" flag first — while it is set their
    // loader deliberately builds no button, so re-running it would change nothing.
    try {
      sessionStorage.removeItem("sea_a11y_icon_closed");
    } catch (e) {
      /* storage blocked; the re-run below may still work */
    }
    // Do NOT inject a second loader. The embed's loader is already on the page and
    // registers idle triggers (scroll / mousedown / touchstart / keydown) that fetch
    // the main bundle — and THIS very tap is one of them, so the widget is already
    // on its way. Injecting a second copy produced two widgets in testing.
    //
    // So just wait for whatever Avada builds, host it, and press it.
    var tries = 0;
    var timer = window.setInterval(function () {
      var el = findAvada();
      if (el) {
        window.clearInterval(timer);
        rehome();
        el.click();
      } else if (++tries > 100) {
        window.clearInterval(timer); // ~10s; CDN blocked or app removed
      }
    }, 100);
  });
})();

/* ------------------------------------------------------------------
   Aurora chat widget — hold the launcher back while the hero is on screen.

   The widget mounts fixed to the bottom of the viewport, which put the chat
   bar on top of the hero's "Try for $9.99" CTA on first paint. The homepage
   template sets data-spark-chat-hold on <html> inline (before the widget's
   own deferred bundle runs, so there is no flash) and styles the hold; this
   releases it once the hero has scrolled out of view.

   Gated on .spark-hero existing, which is the homepage only — every other
   template renders the widget unheld, as before.

   Guarded against a conversation being open: opening the chat panel scrolls
   the page back to top (fixed separately), which re-intersects the hero and
   would otherwise re-apply the hold mid-conversation. The CSS only targets
   the minimized launcher now, but skip/clear the attribute here too so it
   never even sets while a panel is open.
   ------------------------------------------------------------------ */
(function () {
  var root = document.documentElement;
  var HOLD = "data-spark-chat-hold";
  var hero = document.querySelector(".spark-hero");

  // No hero on this page: whatever the template set, get out of the way.
  if (!hero) {
    root.removeAttribute(HOLD);
    return;
  }

  if (!("IntersectionObserver" in window)) {
    root.removeAttribute(HOLD); // no observer: never trap the widget
    return;
  }

  function chatPanelOpen() {
    return !!document.querySelector("#shop-chat-widget .chat-panel.open");
  }

  var io = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i += 1) {
        if (entries[i].isIntersecting && !chatPanelOpen()) root.setAttribute(HOLD, "");
        else root.removeAttribute(HOLD);
      }
    },
    { threshold: 0 }
  );
  io.observe(hero);
})();

/* ------------------------------------------------------------------
   FrontrowMD modal — a "Clinicians' Choice" trust mark
   (snippets/spark-trust-marks.liquid) opens Frontrow's widget: who
   recommends this, their credentials, and what they said.

   Lives here rather than in assets/spark-pdp-buy.js because the mark is
   no longer PDP-only: the homepage carries it too, and that script only
   loads on product templates. One delegated handler for the whole site —
   duplicating it would open two modals on the PDP.

   THEIR MODAL, NOT OURS, WHENEVER THEIRS EXISTS. Where Frontrow's loader
   script is on the page (the PDP, for the trust band's quant widget) it has
   already created its own full-screen modal iframe and opens it on an
   "OPEN_MODAL" post. That page draws its own header, back arrow and close
   button, so wrapping it in a panel of ours produced two nested dialogs.
   Asking them to open it is one dialog and their intended path (it also
   carries their UTM + tracking, which our iframe bypasses).

   Our own overlay is the fallback for pages with no loader — the homepage,
   where the widget itself no longer renders, or a blocked script — where
   posting into the void would leave the mark silently dead.

   Delegated from document so it works no matter when the mark renders.
   ------------------------------------------------------------------ */
(function () {
  var frame = null;
  /* Set by their page's "MODAL_LOADED" post — proof that what is inside our
     fallback frame has its own controls, so ours stays hidden. */
  var theirPageLoaded = false;

  /* The iframe their loader creates: `iframe.frontrow` fixed full-screen,
     display:none until openFrModal() shows it. Its presence is the signal that
     their message listener is installed and will answer "OPEN_MODAL". */
  function theirModal() {
    return document.querySelector("iframe.iframe-modal-frontrow, iframe[class*='iframe-modal-frontrow']");
  }

  function close() {
    if (!frame) return;
    frame.remove();
    frame = null;
    theirPageLoaded = false;
    document.documentElement.style.overflow = "";
  }

  /* NO CHROME OF OURS AROUND THEIR PAGE. This overlay used to be a rounded panel
     on a backdrop with a round × in the corner — around a page that draws its own
     header, its own back arrow and its own ×. Two dialogs, one inside the other,
     which is precisely what the homepage shipped looking like. What is left is a
     full-screen frame and nothing else, so the fallback is visually the same one
     dialog their loader would have opened.

     Our × still exists, hidden, as the way out of a frame that never rendered:
     their page announces itself with a "MODAL_LOADED" post, and if that has not
     arrived a few seconds in, the shopper is looking at a blank rectangle with no
     controls in it and needs a control of ours. On a phone the alternative is
     reloading the site. */
  // Attribute values are concatenated into markup below; escape them so a quote in
  // the value can't end the attribute.
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function open(src) {
    if (!src) return;
    close();
    frame = document.createElement("div");
    frame.className = "spark-fr-modal";
    frame.setAttribute("role", "dialog");
    frame.setAttribute("aria-modal", "true");
    frame.setAttribute("aria-label", "Clinicians who recommend this product");
    frame.innerHTML =
      '<iframe class="spark-fr-modal__frame" src="' + escapeHtml(src) + '" title="Clinicians who recommend this product"></iframe>' +
      '<button type="button" class="spark-fr-modal__close" aria-label="Close" data-close hidden>&times;</button>';
    document.body.appendChild(frame);
    /* Lock the page behind the dialog, or a scroll gesture over the frame moves
       the page the shopper is trying to keep their place in. */
    document.documentElement.style.overflow = "hidden";
    frame.addEventListener("click", function (e) {
      if (e.target.hasAttribute("data-close")) close();
    });
    setTimeout(function () {
      if (!frame || theirPageLoaded) return;
      var btn = frame.querySelector(".spark-fr-modal__close");
      if (btn) {
        btn.hidden = false;
        btn.focus();
      }
    }, 4000);
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest("[data-spark-fr-open]");
    if (!btn) return;

    /* Their listener takes a bare string, not an object — see their loader:
       `if (ev.data === "OPEN_MODAL") return openFrModal(...)`. */
    if (theirModal()) {
      window.postMessage("OPEN_MODAL", "*");
      return;
    }

    /* data-fr-wait: this page carries their loader (see the `loader` param in
       snippets/spark-trust-marks.liquid), so their modal is coming — it is async
       and the tap simply beat it. Waiting a couple of seconds for the dialog that
       belongs here beats opening ours over the top of it. */
    if (btn.hasAttribute("data-fr-wait")) {
      var waited = 0;
      var poll = setInterval(function () {
        waited += 100;
        if (theirModal()) {
          clearInterval(poll);
          window.postMessage("OPEN_MODAL", "*");
        } else if (waited >= 2500) {
          clearInterval(poll);
          open(btn.getAttribute("data-fr-src"));
        }
      }, 100);
      return;
    }

    open(btn.getAttribute("data-fr-src"));
  });

  window.addEventListener("message", function (e) {
    if (String(e.origin).indexOf("thefrontrowhealth") === -1) return;
    /* Their page posts bare strings: "MODAL_LOADED" once it has drawn itself,
       "CLOSE_ALL_MODALS" when the shopper uses ITS close button — which is the
       only close button on screen now, so it has to close our frame too. */
    if (e.data === "MODAL_LOADED") theirPageLoaded = true;
    if (e.data === "CLOSE_ALL_MODALS") close();
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") close();
  });
})();
